import React from 'react'
import env from './../../../../environments/environment'
import useRecruitingInterview, {
  FALLBACK_RECRUITING_LINK,
} from './../../../../shared/hooks/useRecruitingInterview.hook'
import {
  ArrowIcon,
  BoxIcon,
  BrushBand,
  ChevronIcon,
  ContractIcon,
  CornerStripes,
  DollarIcon,
  GrowthIcon,
  PlaneIcon,
  StarIcon,
  TargetIcon,
  TrophyIcon,
  UsabgLogo,
} from './CareersWhyChooseUSABG.icons'
import './CareersWhyChooseUSABG.component.scss'

const cdnImg = file =>
  `https://firebasestorage.googleapis.com/v0/b/${env.integrations.firebase.config.storageBucket}/o/static%2Fwebsite%2F${file}?alt=media`

const PHOTOS = {
  team: { src: cdnImg('img.careers-why-1.jpg'), width: 1920, height: 1281 },
  ring: { src: cdnImg('img.careers-why-2.jpg'), width: 1920, height: 1280 },
  training: { src: cdnImg('img.careers-why-3.jpg'), width: 1920, height: 1280 },
  gala: { src: cdnImg('img.careers-why-4.jpg'), width: 1920, height: 1508 },
  event: { src: cdnImg('img.careers-why-5.jpg'), width: 1920, height: 1280 },
  tumblers: { src: cdnImg('img.careers-why-6.jpg'), width: 1920, height: 1280 },
}

// Desktop flanks the benefits with two photo columns; tablet and phone show a
// pair above the benefits and a strip of four below.
const GALLERIES = [
  { key: 'left', photos: ['team', 'ring', 'training'] },
  { key: 'right', photos: ['gala', 'event', 'tumblers'] },
  { key: 'top', photos: ['team', 'gala'] },
  { key: 'strip', photos: ['ring', 'training', 'tumblers', 'event'] },
]

const BENEFITS = [
  {
    Icon: GrowthIcon,
    title: 'Growth-Focused Culture',
    body: 'We are built for expansion — personally and professionally. When you grow, we grow.',
  },
  {
    Icon: DollarIcon,
    title: 'More Agents. More Production. More Income.',
    body: 'Our model is designed so agents make more money because of the way we serve clients — period.',
  },
  {
    Icon: BoxIcon,
    title: 'Agency-In-A-Box',
    body: 'Plug into a turnkey operation with systems, training, support, contracts, and leadership already in place. You focus on production — we handle the infrastructure.',
  },
  {
    Icon: ContractIcon,
    title: 'One-Stop Shop Contracts',
    body: 'Health. Life. Senior. U65. ACA. Medicare. Everything you need. One partnership.',
  },
  {
    Icon: StarIcon,
    title: 'USABG BUCK$',
    body: 'Production incentives. Bonus opportunities. Recognition.\nWe reward performance — and we do it big.',
  },
  {
    Icon: TargetIcon,
    title: 'PGR – Personal Growth Report',
    body: 'We don’t just track production. We develop leaders.\nOur Personal Growth Report helps you measure progress beyond commissions.',
  },
  {
    Icon: TrophyIcon,
    title: 'Top FMO in the U65 Industry',
    body: 'National footprint. Elite carrier relationships. Serious production power.',
  },
  {
    Icon: PlaneIcon,
    title: 'We Dominate Carrier Trips',
    body: 'If there’s a stage, a spotlight, or a destination involved — our agents are on it.',
  },
]

const Gallery = ({ variant, photos }) => (
  <aside className={`careers-why-choose__gallery careers-why-choose__gallery--${variant}`}>
    {photos.map(name => (
      <figure key={name} className='careers-why-choose__photo'>
        <img src={PHOTOS[name].src} width={PHOTOS[name].width} height={PHOTOS[name].height} alt='' loading='lazy' />
      </figure>
    ))}
  </aside>
)

const CareersWhyChooseUSABG = () => {
  const { loading, goToInterview } = useRecruitingInterview()

  return (
    <section className='careers-why-choose' aria-labelledby='careers-why-choose-title'>
      <CornerStripes className='careers-why-choose__stripes careers-why-choose__stripes--top' />
      <CornerStripes className='careers-why-choose__stripes careers-why-choose__stripes--bottom' />

      <div className='careers-why-choose__layout'>
        <header className='careers-why-choose__header'>
          <div className='careers-why-choose__brand-row'>
            <UsabgLogo className='careers-why-choose__logo careers-why-choose__logo--header' />
            <p className='careers-why-choose__eyebrow'>Why Choose USABG?</p>
          </div>

          <h2 id='careers-why-choose-title' className='careers-why-choose__title'>
            <span className='careers-why-choose__title-line'>
              You Bring the <em className='careers-why-choose__script'>Ambition.</em>
            </span>
            <span className='careers-why-choose__title-line'>
              We Bring the <em className='careers-why-choose__script careers-why-choose__script--red'>Advantage.</em>
            </span>
          </h2>

          <p className='careers-why-choose__intro'>
            Join a team that&rsquo;s built to help you grow &mdash; with the people, tools, and opportunities to take
            your career to the next level.
          </p>
        </header>

        {GALLERIES.map(({ key, photos }) => (
          <Gallery key={key} variant={key} photos={photos} />
        ))}

        <ul className='careers-why-choose__benefits' role='list'>
          {BENEFITS.map(({ Icon, title, body }) => (
            <li key={title} className='careers-why-choose__benefit'>
              <span className='careers-why-choose__benefit-icon'>
                <Icon />
              </span>
              <h3 className='careers-why-choose__benefit-title'>{title}</h3>
              <p className='careers-why-choose__benefit-text'>
                {body.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </p>
            </li>
          ))}
        </ul>

        <footer className='careers-why-choose__footer'>
          <div className='careers-why-choose__banner'>
            <BrushBand className='careers-why-choose__banner-brush' filterId='careers-why-choose-brush' />
            <p className='careers-why-choose__banner-script'>Real People. Real Opportunities.</p>
            <p className='careers-why-choose__banner-caption'>Join USABG and be part of something bigger.</p>
          </div>

          <div className='careers-why-choose__actions'>
            <UsabgLogo className='careers-why-choose__logo careers-why-choose__logo--watermark' />
            <a
              className='careers-why-choose__btn careers-why-choose__btn--primary'
              href={FALLBACK_RECRUITING_LINK}
              onClick={goToInterview}
              aria-busy={loading}
            >
              {loading ? 'Finding a recruiter…' : 'Schedule an Interview'}
              <ChevronIcon />
            </a>
            <a
              className='careers-why-choose__btn careers-why-choose__btn--outline'
              href='https://vimeo.com/showcase/8843553'
              target='_blank'
              rel='noopener noreferrer'
              aria-label='Hear From Our Agents (opens agent testimonials in a new tab)'
            >
              Hear From Our Agents
              <ArrowIcon />
            </a>
          </div>
        </footer>
      </div>
    </section>
  )
}

export default CareersWhyChooseUSABG
